
import React from 'react';
import './styles/Home.css'
import Products from '../components/Products';
import Hero from '../components/Hero';
import ShopCategories from '../components/ShopCategories';
import { useStore } from "../context/StoreContext"; // ✅ Use StoreContext
import AboutPreview from '../components/AboutPreview';
import HowItWorks from '../components/HowItWorks';
import ProcessPreview from '../components/ProcessPreview';
import SEO from '../seo/SEO';
import config from '../config/config';
import ProductCard from '../components/ProductCard';

const Home = () => {
    const { products, categories } = useStore();

    const availableProducts = products.filter(p => p.stock > 0);


    // ✅ Filter for "New Releases" (assuming newest products are first)
    const newReleases = availableProducts.slice(0, 5);


    const featuredProducts = availableProducts.filter(p => p.featured === 1);


    const nameCounts = featuredProducts.reduce((acc, p) => {
        acc[p.name] = (acc[p.name] || 0) + 1;
        return acc;
        }, {});

    const duplicateNames = new Set(
        Object.entries(nameCounts)
            .filter(([_, count]) => count > 1)
            .map(([name]) => name)
    );


    return (
        <>

            <SEO
                title="Withrow Knives - Custom Handmade Knives & Leather Sheaths"
                description="Explore Withrow Knives' custom handmade knives, each paired with a premium leather sheath. Designed for collectors, outdoor enthusiasts, and knife aficionados."
                keywords={[
                "custom knives",
                "handmade knives",
                "premium knives",
                "knife shop",
                "survival knives",
                "hunting knives",
                "handcrafted knives",
                "custom leather sheaths"
                ]}
                url={config.metadata.siteUrl}
            />


            
            <div className='page-wrapper home'>



                <div className='home-sections-wrapper'>
                    <Hero />

                    <section className='about-preview-section' aria-labelledby="about-preview-title">
                        <h2 id="about-preview-title" className='section-title about'>About Our Craft</h2>
                        <AboutPreview />
                    </section>
                    <section className='process-preview-section' aria-labelledby="process-preview-title">
                        <h2 id="process-preview-title" className='section-title process'>The Process</h2>
                        <ProcessPreview />
                    </section>
                </div>

                {categories.length > 1 && (
                <section className='shop-categories' aria-labelledby="shop-categories-title">
                    <h2 id="shop-categories-title" className='section-title'>Shop by Category</h2>
                    <p className='section-description'>Find the perfect knife for your collection, outdoor adventures, or everyday carry.</p>
                    <ShopCategories />
                </section>
                )}


                {featuredProducts.length > 0 && (
                <section className="featured-products" aria-labelledby="featured-products-title">
                    <h2 id="featured-products-title" className="section-title">
                    {featuredProducts.length === 1 ? "Featured Blade" : "Featured Blades"}
                    </h2>

                    <p className="section-description">
                    {featuredProducts.length === 1
                        ? "A unique knife we’re excited to showcase."
                        : "A curated selection of standout knives."}
                    </p>

                    {/* 1-2 items → render cards directly */}
                    {featuredProducts.length <= 3 ? (
                    <div className="featured-card-row">
                        {featuredProducts.map((product, index) => (
                        <ProductCard 
                            key={product.id}
                            product={product}
                            index_position={index + 1}
                            listName="Featured"
                            product_type="Knives"
                            showId={duplicateNames.has(product.name)}
                        />
                        ))}
                    </div>
                    ) : (
                    // 3+ items → use full Products component
                    <Products
                        products={featuredProducts}
                        listName="Featured Products"
                        showNoProductsMessage={false}
                    />
                    )}
                </section>
                )}


                {newReleases.length > 0 && (
                <section className="new-releases" aria-labelledby="new-releases-title">
                    <h2 id="new-releases-title" className="section-title">New Releases</h2>
                    <p className="section-description">Check out our latest arrivals. Fresh off the bench.</p>

                    {/* 1–2 items → simple row */}
                    {newReleases.length <= 2 ? (
                    <div className="featured-card-row">
                        {newReleases.map((product, index) => (
                        <ProductCard
                            key={product.id}
                            product={product}
                            index_position={index + 1}
                            listName="New Releases"
                            product_type="Knives"
                        />
                        ))}
                    </div>
                    ) : (
                    /* 3+ items → full Products component */
                    <Products
                        products={newReleases}
                        listName="New Releases"
                        showNoProductsMessage={false}
                    />
                    )}
                </section>
                )}


                <section className='how-it-works' aria-labelledby="how-it-works-title">
                    <h2 id="how-it-works-title" className='section-title'>How It Works</h2>
                    <p className='section-description'>Purchasing your favorite knife is simple, secure, and easy.</p>
                    <HowItWorks />
                </section>

            </div>
        </>
    );
}

export default Home;